/**
 * /blog/<slug> (and /zh/blog/<slug>) — individual blog post renderer.
 * Looks up the slug in the manifest, renders the markdown body via
 * react-markdown. Falls back to a 404-shaped page if the slug doesn't
 * resolve (so a typo'd link reads as "post not found" rather than
 * crashing the app).
 *
 * locale: 'zh' renders the post's translation when present (else falls
 * back to English content), localizes the chrome, and emits hreflang.
 */
import ReactMarkdown from 'react-markdown';
import { postBySlug, POSTS, localized } from '../content/blog/manifest';
import { MarketingShell } from './MarketingShell';

interface Props {
  slug: string;
  /** 'zh' shows the translation when available, else English. Default 'en'. */
  locale?: 'en' | 'zh';
}

export function BlogPost({ slug, locale = 'en' }: Props) {
  const zh = locale === 'zh';
  const post = postBySlug(slug);

  if (!post) {
    return (
      <MarketingShell
        documentTitle="Post not found · Draftlize Blog"
        documentDescription="That blog post doesn't exist."
      >
        <section className="hero" style={{ paddingTop: 88, paddingBottom: 88 }}>
          <div style={{ maxWidth: 720 }}>
            <span className="label label-amber">404</span>
            <h1
              className="serif"
              style={{
                fontSize: 'clamp(40px, 5vw, 64px)',
                lineHeight: 1,
                margin: '12px 0 24px',
              }}
            >
              {zh ? '文章不存在。' : 'Post not found.'}
            </h1>
            <p className="lede">
              {zh ? '这篇文章不存在。其它文章：' : 'That post doesn’t exist. Other posts:'}
            </p>
            <ul style={{ marginTop: 24, fontSize: 15, lineHeight: 1.7 }}>
              {POSTS.map((p) => {
                const L = localized(p, locale);
                const href = zh && p.zh ? `/zh/blog/${p.slug}` : `/blog/${p.slug}`;
                return (
                  <li key={p.slug}>
                    <a href={href}>{L.title}</a>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </MarketingShell>
    );
  }

  const L = localized(post, locale);
  // hreflang only when this post actually has a zh translation — else
  // there's no alternate URL to advertise.
  const hreflangPath = post.zh ? `/blog/${post.slug}` : undefined;

  return (
    <MarketingShell
      currentLocale={locale}
      hreflangPath={hreflangPath}
      documentTitle={`${L.title} · Draftlize Blog`}
      documentDescription={L.excerpt}
    >
      <article
        style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px 96px' }}
        className="blog-article"
      >
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            letterSpacing: '.12em',
            color: 'var(--ink-3)',
            marginBottom: 16,
          }}
        >
          <a
            href={zh ? '/zh/blog' : '/blog'}
            style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
          >
            {zh ? '← 博客' : '← BLOG'}
          </a>{' '}
          · {post.date} · {post.readTime}
        </div>

        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h1
                className="serif"
                style={{
                  fontSize: 'clamp(32px, 4vw, 48px)',
                  lineHeight: 1.1,
                  fontWeight: 500,
                  letterSpacing: '-.018em',
                  margin: '0 0 32px',
                  color: 'var(--ink-0)',
                }}
              >
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2
                className="serif"
                style={{
                  fontSize: 'clamp(24px, 2.6vw, 30px)',
                  lineHeight: 1.2,
                  fontWeight: 500,
                  letterSpacing: '-.01em',
                  margin: '48px 0 16px',
                  color: 'var(--ink-0)',
                }}
              >
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3
                className="serif"
                style={{
                  fontSize: 20,
                  lineHeight: 1.3,
                  fontWeight: 500,
                  margin: '32px 0 12px',
                  color: 'var(--ink-0)',
                }}
              >
                {children}
              </h3>
            ),
            p: ({ children }) => (
              <p
                style={{
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: 'var(--ink-1)',
                  margin: '0 0 22px',
                }}
              >
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul
                style={{
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: 'var(--ink-1)',
                  margin: '0 0 22px',
                  paddingLeft: 24,
                }}
              >
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol
                style={{
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: 'var(--ink-1)',
                  margin: '0 0 22px',
                  paddingLeft: 24,
                }}
              >
                {children}
              </ol>
            ),
            li: ({ children }) => <li style={{ marginBottom: 8 }}>{children}</li>,
            blockquote: ({ children }) => (
              <blockquote
                style={{
                  borderLeft: '3px solid var(--amber)',
                  padding: '8px 20px',
                  margin: '24px 0',
                  fontStyle: 'italic',
                  color: 'var(--ink-2)',
                  background: 'var(--paper-1)',
                }}
              >
                {children}
              </blockquote>
            ),
            code: ({ children, className }) => {
              const isInline = !className;
              if (isInline) {
                return (
                  <code
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '0.88em',
                      background: 'var(--paper-2)',
                      padding: '2px 6px',
                      color: 'var(--ink-0)',
                    }}
                  >
                    {children}
                  </code>
                );
              }
              return (
                <code
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 13,
                    display: 'block',
                    background: 'var(--paper-2)',
                    padding: '16px 20px',
                    margin: '0 0 22px',
                    lineHeight: 1.5,
                    color: 'var(--ink-0)',
                    overflowX: 'auto',
                  }}
                >
                  {children}
                </code>
              );
            },
            pre: ({ children }) => <>{children}</>,
            a: ({ children, href }) => (
              <a
                href={href}
                style={{
                  color: 'var(--ink-blue)',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                {children}
              </a>
            ),
            em: ({ children }) => (
              <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>
                {children}
              </em>
            ),
            strong: ({ children }) => (
              <strong style={{ color: 'var(--ink-0)', fontWeight: 600 }}>
                {children}
              </strong>
            ),
            hr: () => (
              <hr
                style={{
                  border: 'none',
                  borderTop: '1px solid var(--rule)',
                  margin: '48px 0',
                }}
              />
            ),
          }}
        >
          {L.content}
        </ReactMarkdown>

        <div
          style={{
            marginTop: 64,
            padding: 32,
            background: 'var(--paper-1)',
            border: '1px solid var(--ink-0)',
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 15, color: 'var(--ink-1)', margin: '0 0 16px' }}>
            {zh
              ? '喜欢这种思路？注册送 $5，按量付费，余额永不过期。'
              : 'Like this thinking? New accounts start with $5 free — pay only for what you use, balance never expires.'}
          </p>
          <a className="btn-primary" href="/">
            {zh ? '用 $5 免费开始' : 'Start free with $5'}
          </a>
        </div>
      </article>
    </MarketingShell>
  );
}
